/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DEFAULT_TEXT_DURATION } from '@constants/composition';
import { getFabricObjectByTextLayerId, selectObject } from '@lib/fabricHelper';
import { getNewInstanceTimeslot } from '@helpers/composition';
import { findEmptySlot } from '@helpers/timeline';
import { determineCanvasType } from '@helpers/backgroundInstance';
import { getUniqueId } from '@helpers/footage';
// import { Plus } from '@components/Icons';
import { buildAnimationSettings } from '@helpers/animation';
// import { titleStyles } from '@lib/titleStyles';
import { DraggableTextFootage } from '@components/DraggableFootage';

import TitleThumbnail from './TitleThumbnail';
import './TextTab.scss';

const propTypes = {
  onAdd: PropTypes.func.isRequired,
  expandPool: PropTypes.func.isRequired,
  applyAttributes: PropTypes.func,
  expanded: PropTypes.bool.isRequired,
  composition: PropTypes.object,
  textpresets: PropTypes.object,
  onProgressJump: PropTypes.func,
  onSetDraggingStatus: PropTypes.func,
  deleteTextPreset: PropTypes.func,
  role: PropTypes.number,
  brandFont: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

let scrollInitialized = false;

const TextTab = ({
  onAdd,
  onProgressJump,
  composition,
  brandFont,
  textpresets,
  onSetDraggingStatus,
  deleteTextPreset,
  expanded,
  role,
  applyAttributes,
  expandPool,
  currentTime,
}) => {
  const draggingStyles = (index) => {

    if (textpresets.titleStyles[index]) {
      const preset = textpresets.titleStyles[index].preset.textInstances[0];
      const styles = {
        textAlign: preset.textAlign,
        fontFamily: preset.fontFamily,
        fontSize: preset.fontSize,
        fontWeight: preset.fontWeight,
        fontStyle: preset.fontStyle,
        height: preset.fontSize,
        borderRadius: preset.borderRadius,
        border: preset.border,
        backgroundColor: preset.backgroundColor,
        boxShadow: preset.boxShadow,
        textShadow: preset.textShadow,
        animation: preset.animation,
      };
      return styles;
    }
    return null;
  };
  const [showScroll, setShowScroll] = useState(false);
  const sidebarContextOverlay = document.querySelector(
    '.TextTab .content-grid',
  );
  if (sidebarContextOverlay) {
    setTimeout(() => {
      if (!scrollInitialized) {
        sidebarContextOverlay.scrollTop = 1;
        scrollInitialized = true;
      }
    }, 3000);
  }

  const handleScroll = (e) => {
    const element = e.target;
    if (element.scrollHeight - element.scrollTop === element.clientHeight) {
      setShowScroll(false);
    } else {
      setShowScroll(true);
    }
  };

  const addText = (title, options) => {
    const timeslot = getNewInstanceTimeslot(
      composition.textInstances,
      DEFAULT_TEXT_DURATION,
    );
    if (!timeslot) return;
    const textId = new Date().getTime();
    const newProperties = findEmptySlot(composition, currentTime, currentTime + 3000);
    title.textInstances.forEach((text, index) => {
      if (options) {
        onAdd({
          ...text,
          text: text.text || 'New text',
          initial: text.text || 'New text',
          id: getUniqueId(),
          duration: options.duration,
          visibleFrom: currentTime,
          visibleTo: currentTime + 3000,
          styles: {},
          left: composition.width / 2,
          top: composition.height / 2,
          type: 'text',
          scaleX: text.scaleX || 1,
          scaleY: text.scaleY || 1,
          animation: text.animation && buildAnimationSettings(text.animation.name, {
            visibleFrom: options.visibleFrom,
            visibleTo: options.visibleTo,
            top: composition.height / 2,
          }),
          ang: 0,
          track: newProperties.track - index,
        });
      } else {
        onAdd({
          ...text,
          text: text.text || 'New text',
          initial: text.text || 'New text',
          id: getUniqueId(),
          duration: Math.floor(timeslot.duration),
          visibleFrom: Math.floor(timeslot.visibleFrom),
          visibleTo: Math.floor(timeslot.visibleTo),
          styles: {
            fontFamily: brandFont,
          },
          left: composition.width / 2,
          top: composition.height / 2,
          type: 'text',
          scaleX: text.scaleX || 1,
          scaleY: text.scaleY || 1,
          animation: text.animation && buildAnimationSettings(text.animation.name, {
            visibleFrom: Math.floor(timeslot.visibleFrom),
            visibleTo: Math.floor(timeslot.visibleTo),
            top: composition.height / 2,
          }),
          ang: 0,
          track: newProperties.track - index,
        });
      }
    });
    // Jump to half way of the selected timeslot
    onProgressJump(newProperties.visibleFrom + 1500);
    // Select newly added text
    // setTimeout was needed since Fabric object isn't available immediately after onAdd()
    setTimeout(() => {
      const textlayer = getFabricObjectByTextLayerId(textId);
      selectObject(textlayer);
    });
  };

  const handleAddText = (e) => {
    e.stopPropagation();
    const { name, innerHTML } = e.target;
    const timeslot = getNewInstanceTimeslot(
      composition.textInstances,
      DEFAULT_TEXT_DURATION,
    );

    const initialStyles = {
      id: getUniqueId(),
      text: innerHTML,
      fill: '#ffffff',
      whiteSpace: 'normal',
      overflowWrap: 'normal',
      textAlign: 'center',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: 'normal',
      styles: {},
      duration: Math.floor(timeslot.duration),
      visibleFrom: Math.floor(timeslot.visibleFrom),
      visibleTo: Math.floor(timeslot.visibleTo),
      left: composition.width / 2,
      top: composition.height / 2,
      type: 'text',
      scaleX: 1,
      scaleY: 1,
      ang: 0,
      track: 2,
    };

    switch (name) {
      case 'heading':
        onAdd({
          ...initialStyles,
          fontSize: 150,
          fontFamily: brandFont.primary || 'Open Sans',
        });
        onProgressJump(timeslot.visibleFrom + (timeslot.duration / 2));
        break;
      case 'subheading':
        onAdd({
          ...initialStyles,
          fontFamily: brandFont.secondary || 'Open Sans',
          fontSize: 100,
        });
        onProgressJump(timeslot.visibleFrom + (timeslot.duration / 2));
        break;
      case 'body-text':
        onAdd({
          ...initialStyles,
          fontSize: 80,
          fontFamily: brandFont.tertially || 'Open Sans',
        });
        onProgressJump(timeslot.visibleFrom + (timeslot.duration / 2));
        break;
      default:
    }
  };

  // eslint-disable-next-line consistent-return
  const getWidth = () => {
    const { width, height } = composition;
    if (width === 1920 && height === 1080) {
      return '50%';
    }
    if (width === 1080 && height === 1080) {
      return '33%';
    }
    if (width === 1080 && height === 1920) {
      return '33%';
    }
    if (width === 1080 && height === 1350) {
      return '33%';
    }
  };

  // eslint-disable-next-line consistent-return
  const getHeight = () => {
    const { width, height } = composition;
    if (width === 1920 && height === 1080) {
      return expanded ? 164 : 100;
    }
    if (width === 1080 && height === 1080) {
      return expanded ? 164 : 100;
    }
    if (width === 1080 && height === 1920) {
      return !expanded ? 174 : 310;
    }
    if (width === 1080 && height === 1350) {
      return !expanded ? 130 : 310;
    }
  };
  const titleThumbs = textpresets.titleStyles.map((title, index) => {
    const thisCanvasType = determineCanvasType(
      composition.width,
      composition.height,
    );
    const { canvasType } = title && title.preset;
    if (canvasType !== thisCanvasType) return null;
    return (
      <div
        key={index}
        className="content-grid-col"
        style={{
          width: getWidth(),
          height: `${getHeight() * 0.8}px`,
        }}
      >
        <DraggableTextFootage
          canvas={{
            width: composition.width,
            height: composition.height,
          }}
          duration={composition.duration}
          instance={title}
          list="gallery"
          previewSrc="data:image/png;base64,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"
          draggingStyles={draggingStyles(index)}
          type="text"
          expandPool={expandPool}
          onDragStart={() => {
            onSetDraggingStatus(true);
          }}
          handleAdd={(options) => {
            if (options.empty) {
              // If text preset was dropped over empty slot, add new text
              // addText(title, options);
              addText(title.preset, options);
            } else {
              // if apply styles to...
              options.titleInstance.textInstances.map(textInstance =>
                applyAttributes(textInstance.id, options.styles));
            }
            onSetDraggingStatus(false);
          }}
          onCancel={() => {
            onSetDraggingStatus(false);
          }}
        >
          <TitleThumbnail
            canvas={{
              width: composition.width,
              height: composition.height,
            }}
            title={title}
            onLoad={addText}
            onDeletePreset={deleteTextPreset}
            role={role}
          />
        </DraggableTextFootage>
      </div>
    );
  });
  return (
    <div className="TextTab">
      <section className="VideosTab__heading--section">
        <a
          className="TextTab__addheading DefaultTexts"
          style={{
            fontFamily: brandFont.primary || 'Open Sans',
            fontSize: '20px',
          }}
          name="heading"
          onClick={handleAddText}
        >
          Add Heading
        </a>
        <a
          className="TextTab__addsubheading DefaultTexts"
          style={{
            fontFamily: brandFont.secondary || 'Open Sans',
            fontSize: '16px',
          }}
          name="subheading"
          onClick={handleAddText}
        >
          Add Subheading
        </a>
        <a
          className="TextTab__addbody-text DefaultTexts"
          style={{
            fontFamily: brandFont.tertially || 'Open Sans',
            fontSize: '12px',
          }}
          name="body-text"
          onClick={handleAddText}
        >
          Add Body Text
        </a>
      </section>
      <div className="TextTab__list">
        <div className="content-grid scroll-y" onScroll={handleScroll}>
          {titleThumbs}
          <div
            className="scroll-more"
            style={{ opacity: showScroll ? '1' : '0' }}
          ></div>
        </div>
      </div>
    </div>
  );
};

TextTab.propTypes = propTypes;

export default React.memo(TextTab);
